import type { PriceDetailsQueryParams } from '@/scopes/common/infrastructure/price-details/api/types'
import { useIntl } from '@/intl'
import { space, tok } from '@sunwebgroup/ui-components'
import { Icon, IndeterminateProgressCircle, Sheet, Typography } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { styled } from 'styled-components'
import { usePriceDetails, usePriceDetailsSelect } from '../domain/price-details/hooks'

interface PriceDetailsProps {
  priceDetailsQueryParams: PriceDetailsQueryParams
}

interface PriceDetailsResultProps {
  defaultTitle: string
}

interface PriceDetailsInfoProps {
  defaultTitle: string
  openModal: () => void
  priceDetailsQueryParams: PriceDetailsQueryParams
}

const StyledErrorMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${space('primitives_space_12')};

  text-align: center;
  margin: 0 ${space('primitives_space_16')};
  color: ${tok('semantics_content_secondary_color')};
`

const StyledPriceDetails = styled(Typography)`
  > * table {
    margin-bottom: ${space('primitives_space_12')};
  }

  > * p {
    margin-top: ${space('primitives_space_12')};
    margin-bottom: 0;
  }

  > * b + p {
    margin-top: ${space('primitives_space_4')};
  }

  // only apply margin-top to <b> elements that are not nested inside a <p> element
  > * b:not(p b) {
    display: inline-block;
    margin-top: ${space('primitives_space_12')};
  }
`

function PriceDetailsResult({ defaultTitle }: PriceDetailsResultProps) {
  const intl = useIntl()
  const { isLoading, error, priceDetails } = usePriceDetails()

  return (
    <Sheet.Body side="right">
      <Sheet.Header titleLabel={priceDetails?.title ?? defaultTitle} closeLabel={intl.formatMessage({ id: 'close' })} />
      <Sheet.Content>
        {isLoading && <IndeterminateProgressCircle />}
        {error && (
          <StyledErrorMessage>
            <Icon glyph="ExclamationInTriangle" />
            <Typography as="p" $variant="body-l-bold-snug">
              {intl.formatMessage({ id: 'price_details_error_msg' })}
            </Typography>
          </StyledErrorMessage>
        )}
        {/* TODO: Remove dangerouslySetInnerHTML when new price breakdown design is implemented */}
        {priceDetails && (
          <StyledPriceDetails
            as="div"
            $variant="body-m-regular-snug"
            dangerouslySetInnerHTML={{ __html: priceDetails.content }}
          />
        )}
      </Sheet.Content>
    </Sheet.Body>
  )
}

const StyledButton = styled.button`
  position: relative;
  height: ${space('primitives_space_16')};
  margin-top: ${space('primitives_space_4')};
  color: ${tok('semantics_content_secondary_color')};
`

export function PriceDetailsInfo({ defaultTitle, openModal, priceDetailsQueryParams }: PriceDetailsInfoProps) {
  const { setPriceDetailsQueryParams } = usePriceDetailsSelect()

  const handleOnClick = () => {
    openModal()
    setPriceDetailsQueryParams(priceDetailsQueryParams)
  }

  return (
    <StyledButton onClick={handleOnClick} aria-label={defaultTitle}>
      <Icon glyph="InformationInCircle" size={16} />
    </StyledButton>
  )
}

export function PriceDetails({ priceDetailsQueryParams }: PriceDetailsProps) {
  const intl = useIntl()
  const defaultTitle = intl.formatMessage({ id: 'price_overview' })
  const [open, setOpen] = React.useState(false)

  return (
    <Sheet.Root open={open} onOpenChange={setOpen}>
      <Sheet.Trigger>
        <>
          <PriceDetailsInfo
            openModal={() => setOpen(true)}
            priceDetailsQueryParams={priceDetailsQueryParams}
            defaultTitle={defaultTitle}
          />
          {open && <PriceDetailsResult defaultTitle={defaultTitle} />}
        </>
      </Sheet.Trigger>
    </Sheet.Root>
  )
}
